import React, { useEffect, useRef } from "react";
import JsBarcode from "jsbarcode";

export function BarcodeGraphic({ value, className = "", height = 72, svgRef: externalRef }) {
  const internalRef = useRef(null);
  const svgRef = externalRef || internalRef;
  useEffect(() => {
    if (!svgRef.current || !value) return;
    try {
      JsBarcode(svgRef.current, value, {
        format: "CODE128",
        lineColor: "#111827",
        width: 2,
        height,
        displayValue: false,
        margin: 8,
      });
    } catch {
      if (svgRef.current) svgRef.current.innerHTML = "";
    }
  }, [value, height]);
  return <svg ref={svgRef} className={className} role="img" aria-label={`Code 128 barcode for ${value || "SKU"}`} />;
}

export function BarcodePrintLabel({ product, variant, size, sku }) {
  const displayCode = product?._id || size?.barcode || sku;
  return (
    <div className="barcode-print-area mx-auto w-full max-w-sm border border-gray-300 bg-white p-5 text-center text-gray-900 shadow-sm">
      <p className="text-xs font-bold uppercase tracking-[0.22em]">{product?.brand || "Kanchira"}</p>
      <p className="mt-2 truncate text-base font-semibold">{product?.name || "Jewellery Product"}</p>
      <p className="mt-2 text-xs">SKU: <span className="font-mono font-semibold">{sku}</span></p>
      <BarcodeGraphic value={displayCode} className="mx-auto mt-2 h-auto w-full max-w-xs" height={72} />
      <p className="mt-1 font-mono text-sm tracking-wider">{displayCode}</p>
      <p className="mt-2 text-sm font-semibold">Price: ₹{Number(size?.finalPrice ?? size?.price ?? 0).toLocaleString("en-IN")}</p>
      {variant?.color && <p className="mt-1 text-xs text-gray-600">{variant.color}{size?.size ? ` · ${size.size}` : ""}</p>}
    </div>
  );
}

const escapeHtml = (value) => String(value ?? "").replace(/[&<>"']/g, (character) => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
}[character]));

export function printBarcodeLabel({ svg, product, variant, size, sku }) {
  if (!svg) throw new Error("Barcode is not ready yet.");
  const printWindow = window.open("", "_blank", "width=480,height=640");
  if (!printWindow) throw new Error("Allow pop-ups to print the barcode label.");
  const barcode = new XMLSerializer().serializeToString(svg);
  const price = Number(size?.finalPrice ?? size?.price ?? 0).toLocaleString("en-IN");
  const variantText = [variant?.color, size?.size].filter(Boolean).join(" · ");
  const displayCode = product?._id || size?.barcode || sku;
  printWindow.document.write(`<!doctype html><html><head><title>${escapeHtml(sku)} label</title><style>body{font-family:Arial,sans-serif;margin:0;padding:12mm}.label{width:70mm;margin:auto;text-align:center;color:#111;border:1px solid #ddd;padding:5mm;box-sizing:border-box}.brand{font-size:10px;font-weight:700;letter-spacing:2px;text-transform:uppercase}.name{font-size:15px;font-weight:700;margin:10px 0}.sku{font-family:monospace;font-size:12px}.barcode{width:100%;height:auto;margin-top:8px}.price{font-size:13px;font-weight:700;margin-top:10px}.variant{font-size:10px;margin-top:6px}@media print{body{padding:0}.label{border:0;width:70mm}}</style></head><body><main class="label"><div class="brand">${escapeHtml(product?.brand || "Kanchira")}</div><div class="name">${escapeHtml(product?.name || "Jewellery Product")}</div><div class="sku">SKU: ${escapeHtml(sku)}</div><div class="barcode">${barcode}</div><div class="sku">${escapeHtml(displayCode)}</div><div class="price">Price: ₹${price}</div>${variantText ? `<div class="variant">${escapeHtml(variantText)}</div>` : ""}</main><script>window.onload=()=>setTimeout(()=>window.print(),250);window.onafterprint=()=>window.close();</script></body></html>`);
  printWindow.document.close();
}

export async function downloadBarcodeLabelPng({ svg, product, variant, size, sku }) {
  if (!svg) throw new Error("Barcode is not ready yet.");
  const canvas = document.createElement("canvas");
  canvas.width = 1200;
  canvas.height = 700;
  const context = canvas.getContext("2d");
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = "#111827";
  context.textAlign = "center";
  context.font = "bold 28px Arial";
  context.fillText((product?.brand || "Kanchira").toUpperCase(), 600, 65);
  context.font = "bold 38px Arial";
  context.fillText((product?.name || "Jewellery Product").slice(0, 38), 600, 130);
  context.font = "26px Arial";
  context.fillText(`SKU: ${sku}`, 600, 185);

  const serialized = new XMLSerializer().serializeToString(svg);
  const svgBlob = new Blob([serialized], { type: "image/svg+xml;charset=utf-8" });
  const svgUrl = URL.createObjectURL(svgBlob);
  try {
    const image = new Image();
    await new Promise((resolve, reject) => {
      image.onload = resolve;
      image.onerror = reject;
      image.src = svgUrl;
    });
    const barcodeWidth = Math.min(1000, image.width * 2);
    const barcodeHeight = Math.min(230, image.height * 2);
    context.drawImage(image, (canvas.width - barcodeWidth) / 2, 225, barcodeWidth, barcodeHeight);
  } finally {
    URL.revokeObjectURL(svgUrl);
  }
  context.font = "28px monospace";
  context.fillText(product?._id || size?.barcode || sku, 600, 510);
  context.font = "bold 30px Arial";
  context.fillText(`Price: ₹${Number(size?.finalPrice ?? size?.price ?? 0).toLocaleString("en-IN")}`, 600, 580);
  if (variant?.color) {
    context.font = "22px Arial";
    context.fillText(`${variant.color}${size?.size ? ` · ${size.size}` : ""}`, 600, 630);
  }
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png", 1));
  if (!blob) throw new Error("Unable to create barcode PNG.");
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${sku}-label.png`;
  anchor.click();
  URL.revokeObjectURL(url);
}
